import { Injectable, OnDestroy, inject } from '@angular/core';

import { Fixture } from '@cds/betting-offer';
import { GridViewGroupingConfiguration } from '@cds/betting-offer/grouping/grid-view';
import { EventModel, SportModel } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { GridConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { EventFactory } from '@frontend/sports/grid/core/feature/factories';
import { Column, GridGrouping, GridLayout, GridModel, Group, SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import {
    ColumnActiveState,
    GridStoreService,
    GridStoreState,
    IGridGroup,
    addEvents,
    setActiveGroupState,
    setGroupCollapseThreshold,
    setStateCollapse,
} from '@frontend/sports/grid/core/feature/store';
import { Store } from '@ngrx/store';
import { isNumber, mapValues } from 'lodash-es';
import { Observable, forkJoin, map, of } from 'rxjs';

import { ObservableGrid } from './observable-grid';
import { GridFixturesStatesCounts, GridOption, ObservableGridProvider } from './observable-grid.provider';

@Injectable({ providedIn: 'root' })
export class ObservableGridFactory implements OnDestroy {
    protected grids: ObservableGrid[] = [];

    protected eventFactory = inject(EventFactory);
    protected gridConfig = inject(GridConfig);
    protected sitecore = inject(Sitecore);

    protected store: Store = inject(Store);
    protected gridStore = inject(GridStoreService);
    protected observableGridProvider = inject(ObservableGridProvider);
    protected grouping = inject(OfferGroupingService);

    ngOnDestroy(): void {
        this.destroy();
    }

    destroy(): void {
        this.grids.forEach((grid) => {
            grid.destroy();
        });
        this.grids = [];
    }

    /**
     * Creates a grid model with the provided options and events
     *
     * @param id - is used to identify the grid, so we can wire up the storing of active groups on each market column and the collapse state of several items on the grid
     * @param events - list of the events, can not be empty
     * @param [options={}] - options which are used to build the grid
     * @param [options.activeGrouping] - number specifying which non gridable market group is selected, is being used aso for market template
     * @param [options.marketGrouping] - forwarded to the event mapper, used for non gridable events to apply the event details market grouping
     * @param [options.moreGrouping] - indicates weather the grid should render the more in the market groups dropdown
     * @param [options.grouping] - specifies the grouping approach that will be used for the events
     * @param [options.sorting] - specifies what is the initial sorting applied
     * @param [options.collapsedThreshold] - a number which is being used to collapse the groups after the event count surpasses the threshold
     * @param [options.subscriptionTopic] - specifies what topic should the grid subscribe the events to
     * @returns
     * @memberof ObservableGridFactory
     */
    from(id: string, events: EventModel[], options: GridOption = {}): ObservableGrid {
        if (events.length === 0) {
            throw new Error('Can not map undefined events');
        }

        const grid = this.getBase(id, options, events[0].sport);

        return this.getGrid(grid, options, of(events));
    }

    /**
     * Creates a grid model with the provided options and fixtures
     *
     * @param id - is used to identify the grid, so we can wire up the storing of active groups on each market column and the collapse state of several items on the grid
     * @param fixtures - list of the fixtures, can not be empty
     * @param [options={}] - options which are used to build the grid
     * @param [options.activeGrouping] - number specifying which non gridable market group is selected, is being used aso for market template
     * @param [options.marketGrouping] - forwarded to the event mapper, used for non gridable events to apply the event details market grouping
     * @param [options.moreGrouping] - indicates weather the grid should render the more in the market groups dropdown
     * @param [options.grouping] - specifies the grouping approach that will be used for the events
     * @param [options.sorting] - specifies what is the initial sorting applied
     * @param [options.collapsedThreshold] - a number which is being used to collapse the groups after the event count surpasses the threshold
     * @param [options.subscriptionTopic] - specifies what topic should the grid subscribe the events to
     * @returns
     * @memberof ObservableGridFactory
     */
    fromResponse(
        id: string,
        fixtures?: Fixture[],
        options: GridOption = {},
        isPriceBoosted = false,
        excludePriceboostedMarketGrouping = false,
    ): ObservableGrid {
        if (!fixtures?.length) {
            throw new Error('Can not map undefined fixtures');
        }

        const sport = fixtures[0].sport;
        const events = this.eventFactory.create(fixtures, {
            marketGrouping: options.marketGrouping,
            isPriceBoosted,
            excludePriceboostedMarketGrouping,
        });
        const grid = this.getBase(id, options, {
            id: sport.id,
            name: sport.name.value,
            virtual: fixtures[0].isVirtual,
            isEsport: sport.isEsport,
        });

        return this.getGrid(grid, options, events);
    }

    removeGrid(id: string): void {
        this.grids = this.grids.filter((g) => g.id !== id);
    }

    protected getGrid(grid: GridModel, options: GridOption, events: Observable<EventModel[]>): ObservableGrid {
        // eslint-disable-next-line no-param-reassign
        grid = this.setConfig(grid, options);

        const source = this.getSource(grid, options, events);

        const result = new ObservableGrid(grid, this.store, this.eventFactory, source, () => this.removeGrid(grid.id));
        this.grids.push(result);

        return result;
    }

    private getSource(grid: GridModel, options: GridOption, events: Observable<EventModel[]>): Observable<GridModel> {
        return forkJoin([this.grouping.getGridGrouping(grid.sport.id), events]).pipe(
            map(([config, mapped]) => this.setConfig(grid, options, config, mapped)),
        );
    }

    protected setConfig(grid: GridModel, options: GridOption, config?: GridViewGroupingConfiguration, events?: EventModel[]): GridModel {
        const state = this.gridStore.get(grid.id) || { columns: [], groups: [], events: {} };

        const layout = this.getLayout(config);

        const fixturesStatesCounts: GridFixturesStatesCounts = this.countFixturesStates(events);

        const columns = this.observableGridProvider.getColumns(config, layout, options, fixturesStatesCounts);

        this.stripEventData(events);
        this.setPlaceHolder(options, columns);
        // eslint-disable-next-line no-param-reassign
        grid = this.updateGridState(grid, layout, options, columns, state, events);

        if (config) {
            const active = this.getActiveColumns(columns, state.columns);
            return setActiveGroupState(grid, active);
        }

        return grid;
    }

    protected stripEventData(events?: EventModel[]) {
        if (events?.length) {
            // for state sanity we clean up unneeded properties
            // mainly for event data
            events.forEach((event) => {
                //no
                //@ts-expect-error The operand of a 'delete' operator must be option
                // eslint-disable-next-line no-param-reassign
                delete event.fixtureScoreboard;
                // eslint-disable-next-line no-param-reassign
                delete event.splitFixtureIds;
            });
        }
    }

    protected setPlaceHolder(options: GridOption, columns: Column[]) {
        if (options && options.activeGrouping != null) {
            const placeholder = this.getPlaceholderGroup(options.activeGrouping);

            columns.forEach((column, index) => {
                // eslint-disable-next-line no-param-reassign
                column.enabled = index === 0;

                if (column.enabled) {
                    column.groups.push(placeholder);
                    // eslint-disable-next-line no-param-reassign
                    column.groups.forEach((group) => (group.active = group === placeholder));
                }
            });
        }
    }

    protected updateGridState(
        grid: GridModel,
        layout: GridLayout,
        options: GridOption,
        columns: Column[],
        state: GridStoreState,
        events?: EventModel[],
    ): GridModel {
        const eventState = mapValues(state.events, (event) => ({
            collapsed: event.collapsed,
            collapsedChildren: event.collapsedChildren,
        }));

        // eslint-disable-next-line no-param-reassign
        grid = setStateCollapse({ ...grid, layout, columns }, state.groups, eventState);

        if (events?.length) {
            // eslint-disable-next-line no-param-reassign
            grid = addEvents(grid, events);
            if (options.collapsedThreshold) {
                // eslint-disable-next-line no-param-reassign
                grid = setGroupCollapseThreshold(grid, options.collapsedThreshold);
            }
        }
        return grid;
    }

    protected getBase(id: string, options: GridOption, sport: SportModel, isCustomGrid?: boolean): GridModel {
        let topic = options.subscriptionTopic === undefined ? SubscriptionTopic.Grid : options.subscriptionTopic;

        if (isNumber(options.activeGrouping) || isCustomGrid) {
            // when it is a number we expect to show non gridable markets
            topic = SubscriptionTopic.NonGridable;
        }

        return {
            id,
            grouping: options.grouping === undefined ? GridGrouping.League : options.grouping,
            sorting: options.sorting,
            columns: [],
            sport,
            groups: [],
            groupingThreshold: options.collapsedThreshold,
            layout: GridLayout.Default,
            media: { enabled: false, videoEvent: undefined, animationEvent: undefined, statsEvent: undefined },
            collapsed: {
                groups: [],
                events: {},
            },
            topic,
            topicDetail: options.subscriptionTopicDetail,
            disableGroupSorting: options.disableGroupSorting,
        };
    }

    protected getLayout(config?: GridViewGroupingConfiguration): GridLayout {
        if (config && this.gridConfig.isSixPackLayoutEnabled && config.sixPackGroups?.some((group) => group.marketGroupIds.length > 0)) {
            return GridLayout.SixPack;
        }

        return GridLayout.Default;
    }

    protected getActiveColumns(columns: Column[], state: ColumnActiveState[]): IGridGroup[] {
        return columns
            .map((column, index) => {
                const columnState = state.find((current) => current.index === index);

                if (columnState) {
                    return { columnId: column.id, groupId: columnState.group };
                }
                //removing below return will break build
                return;
            })
            .filter(isDefined);
    }

    private getPlaceholderGroup(id: number | string): Group {
        return {
            id: id.toString(),
            name: this.sitecore.eventGrid.MarketSwitcher_More,
            active: false,
            extended: true,
            visible: false,
            isFallbackGroup: false,
            fallbackGridGroupIds: [],
        };
    }

    private countFixturesStates(events?: EventModel[]): GridFixturesStatesCounts {
        const fixturesStatesCounts: GridFixturesStatesCounts = {
            live: 0,
            preMatch: 0,
        };

        events?.forEach((event) => (event.live ? fixturesStatesCounts.live++ : fixturesStatesCounts.preMatch++));
        return fixturesStatesCounts;
    }
}
